import { useParams, Link } from 'react-router-dom';
import { species } from './foragingData';
import PageHeader from './PageHeader';
import SnailOnly from './SnailOnly';
import MailingListForm from './MailingListForm';
import './ForagingSpeciesPage.css';

function riskClass(risk) {
  const r = risk.toUpperCase();
  if (r.startsWith('VERY HIGH')) return 'very-high';
  if (r.startsWith('HIGH')) return 'high';
  if (r.startsWith('MODERATE')) return 'moderate';
  if (r.startsWith('LOW-MODERATE')) return 'low-moderate';
  if (r.startsWith('LOW')) return 'low';
  return 'none';
}

export default function ForagingSpeciesPage() {
  const { slug } = useParams();
  const sp = species.find(s => s.slug === slug);

  if (!sp) {
    return (
      <div className="fsp-not-found">
        <PageHeader />
        <p>Species not found.</p>
        <Link to="/foraging-guide" className="btn btn-outline-dark">&larr; Back to Foraging Guide</Link>
      </div>
    );
  }

  const [heroImg, ...galleryImgs] = sp.images;

  return (
    <div className="fsp-page">
      <PageHeader />
      <div
        className="fsp-hero"
        style={heroImg ? { backgroundImage: `url(/foraging/${heroImg})` } : undefined}
      >
        {!heroImg && <SnailOnly className="snail-watermark" />}
        <div className="fsp-hero-overlay">
          <Link to="/foraging-guide" className="fsp-back">&larr; Foraging Guide</Link>
        </div>
      </div>

      <div className="fsp-body">
        <div className="fsp-inner">

          <h1 className="fsp-title">{sp.name}</h1>
          <p className="fsp-scientific">{sp.scientific}</p>
          {sp.commonNames && <p className="fsp-common-names">Also known as: {sp.commonNames}</p>}

          <p className="fsp-desc">{sp.description}</p>

          <div className="fsp-section">
            <h3 className="fsp-section-title">When to Forage</h3>
            <p>{sp.whenToForage}</p>
          </div>

          <div className="fsp-section fsp-section--caution">
            <h3 className="fsp-section-title">Cautions</h3>
            <p>{sp.cautions}</p>
          </div>

          <div className="fsp-section">
            <h3 className="fsp-section-title">Nutrition</h3>
            <p>{sp.nutrition}</p>
          </div>

          <div className="fsp-section">
            <h3 className="fsp-section-title">How to Identify</h3>
            <ul className="fsp-list">
              {sp.identify.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>

          {sp.lookalikes.length > 0 && (
            <div className="fsp-section">
              <h3 className="fsp-section-title">Lookalikes</h3>
              <div className="fsp-lookalikes">
                {sp.lookalikes.map((l, i) => (
                  <div className="fsp-lookalike" key={i}>
                    <div className="fsp-lookalike-header">
                      <strong>{l.name}</strong>
                      <span className={`fsp-risk fsp-risk--${riskClass(l.risk)}`}>{l.risk}</span>
                    </div>
                    <p>{l.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="fsp-section">
            <h3 className="fsp-section-title">How to Forage</h3>
            <p>{sp.howToForage}</p>
          </div>

          {sp.recipeTitle && (
            <div className="fsp-section fsp-section--recipe">
              <h3 className="fsp-section-title">Recipe &mdash; {sp.recipeTitle}</h3>
              <p>{sp.recipe}</p>
            </div>
          )}

          {galleryImgs.length > 0 && (
            <div className="fsp-gallery">
              {galleryImgs.map((img, i) => (
                <img key={i} className="fsp-gallery-img" src={`/foraging/${img}`} alt={`${sp.name}`} />
              ))}
            </div>
          )}

          <div className="fsp-stay-connected">
            <h3 className="fsp-stay-connected-title">Want more?</h3>
            <p className="fsp-stay-connected-desc">Join our newsletter for more recipes, foraging guides and upcoming events.</p>
            <MailingListForm />
            <p className="fsp-stay-connected-socials">
              Or follow us on{' '}
              <a href="https://www.instagram.com/subtleanimal/" target="_blank" rel="noopener noreferrer">Instagram</a>
              {' '}and{' '}
              <a href="https://www.facebook.com/profile.php?id=61573240501320" target="_blank" rel="noopener noreferrer">Facebook</a>
              {' '}to discover more recipes and events.
            </p>
          </div>

          <div className="fsp-actions">
            <Link to="/foraging-guide" className="btn btn-outline-dark">&larr; Back to Foraging Guide</Link>
          </div>

        </div>
      </div>
    </div>
  );
}
